/**
 * Scope-doc builder: renders the questionnaire's structured model as a Tiptap ProseMirror JSON
 * doc (H1 = section, H2 = group / scope item, H3 = question) for the deal Scope tab. Pure - the
 * scope model stays the source of truth; this only projects it.
 *
 * Mapping notes:
 * - Every heading carries a `dataKey` attr (HeadingKeys) anchoring it to its model entity:
 *   `section:<sectionKey>` / `group:<groupKey>` / `field:<fieldKey>` / `adhoc:<primaryFieldKey>`.
 *   The harvest (scope-doc-harvest.ts) keys edits off these anchors; a user-added heading has
 *   dataKey null - the "new scope item" signal.
 * - Field values reuse the question view's display resolution (resolveFieldDisplayValue), so both
 *   views always agree on what an answer "looks like".
 * - An ad-hoc group IS one scope item: its H2 is the item, the primary field's discussion text and
 *   scope brief render directly under it (no duplicate H3), with status / work-type / hours chips.
 * - Structured values (enum answers, status/hours chips, brief tags) render as `scopeChip` ATOM
 *   nodes - not editable as text, deletable as a unit - so the harvest never parses half-edited
 *   enum text.
 * - View mode: empty answers render as "-" (surface incompleteness); `answeredOnly` hides
 *   unanswered questions (the question view's filter). Edit mode: every rules-visible question
 *   renders with an empty editable paragraph, and every scope-brief dimension gets a row.
 */

import {
  isAdhocGroupSectionKey,
  isCostedScopeStatus,
  SCOPE_BRIEF,
  SCOPE_STATUS_LABELS,
  type DealScopeUserSection,
  type ScopeMeta,
} from '@protoss/shared';

import type {
  SalesDealQuestionnaireSummary,
  SalesDealTemplateFieldSummary,
  SalesDealTemplateGroupSummary,
  SalesDealTemplateSectionSummary,
} from '../services/salesApi';
import {
  fieldHasVisibleContent,
  resolveFieldDisplayValue,
  type FieldDisplayValue,
} from './field-display-value';
import {
  isFieldVisibleByRules,
  type QuestionnaireAnswerMap,
  type QuestionnaireFieldLookup,
} from './questionnaire-field-visibility';

type TiptapNode = Record<string, unknown>;

export type ScopeDocMode = 'view' | 'edit';

// ── Model anchors (heading dataKey) ──────────────────────────────────────────

export type ScopeDocAnchor =
  | { type: 'section'; key: string }
  | { type: 'group'; key: string }
  | { type: 'field'; key: string }
  | { type: 'adhoc'; key: string }
  /** A user-authored note section (persisted doc-only content; never in the questionnaire). */
  | { type: 'user'; key: string };

export function scopeDocKey(anchor: ScopeDocAnchor): string {
  return `${anchor.type}:${anchor.key}`;
}

export function parseScopeDocKey(dataKey: unknown): ScopeDocAnchor | null {
  if (typeof dataKey !== 'string') return null;
  const idx = dataKey.indexOf(':');
  if (idx <= 0) return null;
  const type = dataKey.slice(0, idx);
  const key = dataKey.slice(idx + 1);
  if (!key) return null;
  if (
    type === 'section' ||
    type === 'group' ||
    type === 'field' ||
    type === 'adhoc' ||
    type === 'user'
  ) {
    return { type, key };
  }
  return null;
}

/**
 * Groups rendered as a Notion-style two-column property panel (propertyList/propertyRow nodes) in
 * BOTH view and edit mode instead of the H3 + paragraph projection. The signal is the group's
 * `groupLayoutHint` (seeded as `property-panel`); the known-key set is a compatibility fallback so
 * existing deals - whose forked templates predate the hint - still get the panel.
 */
export const PROPERTY_PANEL_LAYOUT_HINT = 'property-panel';
const PROPERTY_PANEL_GROUP_KEYS: ReadonlySet<string> = new Set([
  'company_overview',
  'company', // the rillet-family templates' company-info group
  'primary_contact',
]);

function isPropertyPanelGroup(group: SalesDealTemplateGroupSummary): boolean {
  return (
    group.groupLayoutHint === PROPERTY_PANEL_LAYOUT_HINT ||
    PROPERTY_PANEL_GROUP_KEYS.has(group.groupKey)
  );
}

/** Fields hidden in the property panel (see the transitional note in groupNodes). */
const PROPERTY_PANEL_HIDDEN_FIELD_KEYS: ReadonlySet<string> = new Set(['stakeholder_roles_other']);

/** Controls whose free-text rendering round-trips losslessly enough to harvest from the doc. */
export const SCOPE_DOC_TEXT_CONTROLS: ReadonlySet<string> = new Set([
  'text',
  'textarea',
  'autocomplete',
  'number',
  'currency',
  'date',
]);

// ── Node helpers ─────────────────────────────────────────────────────────────

function textRun(text: string, marks?: Array<{ type: string }>): TiptapNode {
  return marks && marks.length > 0 ? { type: 'text', text, marks } : { type: 'text', text };
}

const BOLD = [{ type: 'bold' }];
const ITALIC = [{ type: 'italic' }];

/**
 * Coerce untrusted display data to trimmed text. ProseMirror text nodes must be NON-EMPTY strings -
 * one invalid node makes Tiptap fall back to an empty doc (blanking the whole tab), so every
 * dynamic value (table cells arrive as unchecked JSON, option labels can be empty) goes through
 * this and empty results are skipped by the caller.
 */
function asText(v: unknown): string {
  return (typeof v === 'string' ? v : v == null ? '' : String(v)).trim();
}

function chip(label: string, variant: 'neutral' | 'success' | 'warning' = 'neutral'): TiptapNode {
  return { type: 'scopeChip', attrs: { label, variant } };
}

/**
 * Paragraphs carry an optional `dataKey` row anchor (HeadingKeys stamps paragraphs too):
 * `brief:<dim>` = a scope-brief row (harvested by dim regardless of label-text mangling),
 * `chips:item` / `chips:enum` / `chips:source` = display-only rows (never harvested).
 */
function paragraph(runs: TiptapNode[], dataKey?: string): TiptapNode {
  const node: TiptapNode = runs.length > 0 ? { type: 'paragraph', content: runs } : { type: 'paragraph' };
  if (dataKey) node.attrs = { dataKey };
  return node;
}

function heading(level: 1 | 2 | 3, text: string, dataKey: string | null): TiptapNode {
  return { type: 'heading', attrs: { level, dataKey }, content: [textRun(text)] };
}

/** One paragraph per non-empty line, so multi-line textarea answers keep their breaks. */
function textParagraphs(text: string, mode: ScopeDocMode): TiptapNode[] {
  const lines = text
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);
  if (lines.length === 0) {
    return [mode === 'edit' ? paragraph([]) : paragraph([textRun('-')])];
  }
  return lines.map((l) => paragraph([textRun(l)]));
}

function chipRow(labels: string[], prefix?: TiptapNode[], dataKey?: string): TiptapNode {
  const runs: TiptapNode[] = [...(prefix ?? [])];
  labels
    .map(asText)
    .filter(Boolean)
    .forEach((label) => {
      if (runs.length > 0) runs.push(textRun(' '));
      runs.push(chip(label));
    });
  return paragraph(runs, dataKey);
}

/**
 * A scope-brief row. The dimension LABEL is a FIXED, non-editable KEY - carried as `dataLabel` and
 * rendered by the host as a CSS `::before`, NEVER as editable content - so it can't be edited and
 * never lands in the harvested value. `valueRuns` is the editable value (text or chips). An empty
 * value (edit mode) renders an "Empty" placeholder (CSS). The harvest anchors on the `brief:<dim>`
 * dataKey, so the visible label is purely presentational.
 */
function briefRow(valueRuns: TiptapNode[], dim: (typeof SCOPE_BRIEF)[number]): TiptapNode {
  const node: TiptapNode =
    valueRuns.length > 0 ? { type: 'paragraph', content: valueRuns } : { type: 'paragraph' };
  node.attrs = { dataKey: `brief:${dim.key}`, dataLabel: dim.label };
  return node;
}

function tableNode(dv: Extract<FieldDisplayValue, { kind: 'table' }>): TiptapNode {
  const headerRow: TiptapNode = {
    type: 'tableRow',
    content: dv.columns.map((col) => {
      const label = asText(col.label);
      return {
        type: 'tableHeader',
        content: [paragraph(label ? [textRun(label)] : [])],
      };
    }),
  };
  const rows: TiptapNode[] = dv.rows.map((row) => ({
    type: 'tableRow',
    content: dv.columns.map((col) => {
      // Cells arrive as unchecked JSON (typed Record<string, string> but AI/legacy writes can hold
      // numbers/objects) - coerce, and give empty cells an empty paragraph.
      const cell = asText(row[col.key]);
      return {
        type: 'tableCell',
        content: [paragraph(cell ? [textRun(cell)] : [])],
      };
    }),
  }));
  return { type: 'table', content: [headerRow, ...rows] };
}

function valueNodes(dv: FieldDisplayValue, mode: ScopeDocMode): TiptapNode[] {
  switch (dv.kind) {
    case 'text':
      return textParagraphs(dv.text, mode);
    case 'pills':
      return [chipRow(dv.pills, undefined, 'chips:enum')];
    case 'table':
      return dv.columns.length > 0
        ? [tableNode(dv)]
        : [mode === 'edit' ? paragraph([]) : paragraph([textRun('-')])];
    case 'empty':
      return [mode === 'edit' ? paragraph([]) : paragraph([textRun('-')])];
  }
}

// ── Scope item (ad-hoc primary) rendering ────────────────────────────────────

/**
 * Status chip label + variant. The LABEL is the canonical `SCOPE_STATUS_LABELS` (same copy as the
 * questionnaire's Scope control - Requested / Ongoing discussion / Not included in estimate); the
 * VARIANT is the Scope-doc colour: costed = green, "Ongoing discussion" = amber, "Not included in
 * estimate" = neutral grey.
 */
export function scopeStatusChip(scope: ScopeMeta | undefined): {
  label: string;
  variant: 'neutral' | 'success' | 'warning';
} {
  const status = scope?.scopeStatus ?? 'requested';
  const label = SCOPE_STATUS_LABELS[status];
  if (status === 'mentioned') return { label, variant: 'neutral' };
  if (status === 'ongoing') return { label, variant: 'warning' };
  return { label, variant: 'success' };
}

function scopeItemNodes(
  field: SalesDealTemplateFieldSummary,
  answers: QuestionnaireAnswerMap,
  hoursByField: Map<string, number> | undefined,
  mode: ScopeDocMode
): TiptapNode[] {
  const nodes: TiptapNode[] = [];
  const scope = field.scopeMeta;

  // Hours as a single pill right after the H2 - costed items only (a not-costed item is 0h, so the
  // chip is noise). Status + Activities live in the property table below (with the brief), so the
  // narrative ("what was discussed") stays right under the heading.
  const hours = hoursByField?.get(field.fieldKey);
  if (hours != null && isCostedScopeStatus(scope?.scopeStatus)) {
    nodes.push(paragraph([chip(`${Math.round(hours)}h`)], 'chips:item'));
  }

  // What was discussed (the primary field's own value) - the editable narrative, right after the H2.
  const dv = resolveFieldDisplayValue(field, answers);
  nodes.push(...textParagraphs(dv.kind === 'text' ? dv.text : '', mode));

  // Transcript provenance ("where did this come from") - VIEW ONLY and never harvested:
  // provenance is verified server-side and must not be user-authored (see ScopeMeta.sourceRefs).
  // One row per verified quote, naming its source document when recorded; legacy items fall
  // back to the single sourceQuote.
  if (mode === 'view') {
    const refs = scope?.sourceRefs?.length
      ? scope.sourceRefs
      : scope?.sourceQuote
        ? [{ documentUuid: '', documentTitle: undefined, quote: scope.sourceQuote }]
        : [];
    for (const ref of refs) {
      const quote = asText(ref.quote);
      if (!quote) continue;
      const runs = [
        textRun('From the transcript: ', BOLD),
        textRun(`"${quote}"`, ITALIC),
      ];
      const docTitle = asText(ref.documentTitle);
      if (docTitle) runs.push(textRun(` - ${docTitle}`));
      nodes.push(paragraph(runs, 'chips:source'));
    }
  }

  // Item meta (Scope status + Activities) as live-editable property rows heading the SAME 2-column
  // table as the scope-brief rows below - see DealScopePane.renderRow -> ScopeItemMetaRow. Not
  // harvested (the controls live-save to scope_meta).
  nodes.push({
    type: 'propertyList',
    content: [
      { type: 'propertyRow', attrs: { fieldKey: `scopemeta:${field.fieldKey}:scopeStatus` } },
      { type: 'propertyRow', attrs: { fieldKey: `scopemeta:${field.fieldKey}:workType` } },
    ],
  });

  // Scope brief dimensions in SCOPE_BRIEF order. TEXT dims (problem / solution / outcome / notes) stay
  // editable-in-doc `brief:<dim>` paragraphs (harvested on Save; the label is a ::before key, the
  // value the editable content, continuation lines share the anchor). TAG dims (in/out scope,
  // complexity, assumptions) render as live-editable creatable multi-select property controls
  // (`scopemeta:` rows -> ScopeItemMetaRow), batched into ONE property list. View mode hides empties.
  let tagRows: TiptapNode[] = [];
  const flushTags = () => {
    if (tagRows.length > 0) {
      nodes.push({ type: 'propertyList', content: tagRows });
      tagRows = [];
    }
  };
  for (const dim of SCOPE_BRIEF) {
    const v = scope?.[dim.key];
    if (dim.control === 'tags') {
      const items = Array.isArray(v) ? (v as string[]).filter(Boolean) : [];
      if (items.length === 0 && mode === 'view') continue;
      tagRows.push({
        type: 'propertyRow',
        attrs: { fieldKey: `scopemeta:${field.fieldKey}:${dim.key}` },
      });
      continue;
    }
    flushTags();
    const rowKey = `brief:${dim.key}`;
    const lines = (typeof v === 'string' ? v : '')
      .split('\n')
      .map((l) => l.trim())
      .filter(Boolean);
    if (lines.length === 0 && mode === 'view') continue;
    nodes.push(briefRow(lines.length > 0 ? [textRun(lines[0]!)] : [], dim));
    for (const line of lines.slice(1)) nodes.push(paragraph([textRun(line)], rowKey));
  }
  flushTags();
  return nodes;
}

// ── Doc builder ──────────────────────────────────────────────────────────────

export interface ScopeDocInput {
  questionnaire: SalesDealQuestionnaireSummary;
  answers: QuestionnaireAnswerMap;
  fieldLookup?: QuestionnaireFieldLookup;
  /** Per-line-item base hours from the saved estimate (see buildHoursByField). */
  hoursByField?: Map<string, number>;
  /** 'view' (default) = read-only projection; 'edit' = editable doc (all fields, brief rows). */
  mode?: ScopeDocMode;
  /** View mode only: hide questions with no answer/brief (the question view's filter). */
  answeredOnly?: boolean;
  /**
   * User-authored note sections (from questionnaire.userSections) - re-emitted at the doc END
   * (v1 position rule) with their H1 re-stamped `user:<key>` so they round-trip through the
   * harvest without touching the questionnaire model.
   */
  userSections?: DealScopeUserSection[];
}

function byDisplayOrder<T extends { displayOrder: number }>(items: readonly T[]): T[] {
  return [...items].sort((a, b) => a.displayOrder - b.displayOrder);
}

function groupNodes(
  group: SalesDealTemplateGroupSummary,
  input: ScopeDocInput,
  mode: ScopeDocMode
): TiptapNode[] {
  const { questionnaire, answers, fieldLookup } = input;
  // Property-panel groups render EVERY rules-visible field (the answered-vs-empty split is a runtime
  // decision in the panel's node view, not a build-time filter), so `answeredOnly` never drops rows
  // here - dropping them would make "Show all" unable to reveal an empty field to fill.
  const isPanel = isPropertyPanelGroup(group);
  const visibleFields = byDisplayOrder(group.fields).filter((field) => {
    // Transitionally hidden in the panel: the legacy free-text "specify" companion of a now-creatable
    // multi-select (stakeholder_roles). New templates drop the field; existing forked deals still
    // carry it, so hide it here (custom roles are added via the "+ Add option" flow instead).
    if (isPanel && PROPERTY_PANEL_HIDDEN_FIELD_KEYS.has(field.fieldKey)) return false;
    if (
      !isFieldVisibleByRules(field.fieldKey, questionnaire.conditionalRules, answers, fieldLookup)
    ) {
      return false;
    }
    if (!isPanel && mode === 'view' && input.answeredOnly) {
      return fieldHasVisibleContent(field, answers);
    }
    return true;
  });
  if (visibleFields.length === 0) return [];

  // Notion-style property panel: keep the group H2, then one propertyList of propertyRow atoms (label
  // + live control rendered by the host). No per-field H3, so these questions lose the collapse
  // chevron by design - the 2-column layout replaces it.
  if (isPanel) {
    const panelNodes: TiptapNode[] = [];
    if (group.label) {
      panelNodes.push(
        heading(2, group.label, scopeDocKey({ type: 'group', key: group.groupKey }))
      );
    }
    panelNodes.push({
      type: 'propertyList',
      content: visibleFields.map((field) => ({
        type: 'propertyRow',
        attrs: { fieldKey: field.fieldKey },
      })),
    });
    return panelNodes;
  }

  const primary = visibleFields.find((f) => f.isAdhocPrimary);
  const nodes: TiptapNode[] = [];
  if (group.label) {
    const h2 = heading(
      2,
      group.label,
      primary
        ? scopeDocKey({ type: 'adhoc', key: primary.fieldKey })
        : scopeDocKey({ type: 'group', key: group.groupKey })
    );
    // Mark a not-costed scope item's heading with its status (data-status) so CSS can style it. Only
    // "Not included in estimate" (mentioned) actually strikes through - "Ongoing discussion" (ongoing)
    // is deferred but still live, so it keeps its amber pill and is left un-struck (see deal-page.css).
    const status = primary?.scopeMeta?.scopeStatus;
    if (status && !isCostedScopeStatus(status)) {
      (h2.attrs as Record<string, unknown>).dataStatus = status;
    }
    nodes.push(h2);
  }
  for (const field of visibleFields) {
    if (field.isAdhocPrimary) {
      // The H2 already names the scope item - render its content directly (no duplicate H3).
      nodes.push(...scopeItemNodes(field, answers, input.hoursByField, mode));
      continue;
    }
    if (field.label) {
      nodes.push(heading(3, field.label, scopeDocKey({ type: 'field', key: field.fieldKey })));
    }
    nodes.push(...valueNodes(resolveFieldDisplayValue(field, answers), mode));
  }
  return nodes;
}

function sectionNodes(
  section: SalesDealTemplateSectionSummary,
  input: ScopeDocInput,
  mode: ScopeDocMode
): TiptapNode[] {
  const body = byDisplayOrder(section.groups).flatMap((group) => groupNodes(group, input, mode));
  if (body.length === 0 && mode === 'view') return [];
  return [
    heading(
      1,
      section.label || section.sectionKey,
      scopeDocKey({ type: 'section', key: section.sectionKey })
    ),
    ...body,
  ];
}

/**
 * Build the Scope-tab Tiptap doc from the questionnaire model. Returns null when there is
 * nothing to render (no sections / every field hidden), so callers can show an empty hint.
 */
export function buildScopeDoc(input: ScopeDocInput): Record<string, unknown> | null {
  const mode: ScopeDocMode = input.mode ?? 'view';
  const sections = byDisplayOrder(input.questionnaire.sections);
  const content = sections.flatMap((section) => sectionNodes(section, input, mode));
  // No synthetic catch-all: functional areas are the ONLY capture container. The "Ad-hoc questions"
  // section renders only when the server still materializes it (deals with legacy ungrouped
  // questions); new questions arrive via /problem inside an area or /functional-area.
  // User-authored note sections re-emit at the doc END, in BOTH modes (v1 position rule: user
  // sections always append after the model sections). Blocks are stored verbatim from the editor's
  // getJSON(); only the section H1 gets its `user:<key>` anchor re-stamped (merged into existing
  // attrs) so the harvest recognizes the section and the no-op diff stays stable.
  for (const us of input.userSections ?? []) {
    if (!Array.isArray(us.blocks) || us.blocks.length === 0) continue;
    content.push(
      ...us.blocks.map((block, i) => {
        const b = block as TiptapNode;
        if (i === 0 && b.type === 'heading') {
          return {
            ...b,
            attrs: { ...(b.attrs ?? {}), dataKey: scopeDocKey({ type: 'user', key: us.key }) },
          } as TiptapNode;
        }
        return b;
      })
    );
  }
  if (content.length === 0) return null;
  return { type: 'doc', content };
}

/**
 * The heading texts of every MODEL-anchored heading in a built Scope doc - the LockHeadings set
 * (template structure is read-only; user-added headings stay editable). EXCEPTIONS left UNLOCKED so
 * the user can rename them: functional-area (Business-Process) section H1s - the rename is harvested
 * into a Business-Process re-tag of that section's items; and ad-hoc scope ITEM H2s (`adhoc:`) - the
 * rename is harvested into that item's label (updateLabel), keeping its fieldKey stable.
 */
export function collectScopeDocLockedHeadings(doc: Record<string, unknown> | null): string[] {
  if (!doc) return [];
  const out: string[] = [];
  const walk = (node: unknown): void => {
    if (!node || typeof node !== 'object') return;
    const n = node as { type?: unknown; attrs?: { dataKey?: unknown }; content?: unknown[] };
    if (n.type === 'heading' && n.attrs && typeof n.attrs.dataKey === 'string' && n.attrs.dataKey) {
      const anchor = parseScopeDocKey(n.attrs.dataKey);
      const isFunctionalArea = anchor?.type === 'section' && isAdhocGroupSectionKey(anchor.key);
      const isAdhocItem = anchor?.type === 'adhoc';
      // User note sections are the user's own content - always renamable.
      const isUserSection = anchor?.type === 'user';
      if (!isFunctionalArea && !isAdhocItem && !isUserSection) {
        const text = (n.content ?? [])
          .map((c) =>
            c && typeof c === 'object' && 'text' in c
              ? String((c as { text: unknown }).text ?? '')
              : ''
          )
          .join('')
          .trim();
        if (text) out.push(text);
      }
    }
    if (Array.isArray(n.content)) n.content.forEach(walk);
  };
  walk(doc);
  return out;
}
